import { createFromBaseNpcContext } from '../janus-engine/compile/from-base-npc-stages';
import { createNpcWornInventoryProjector } from '../janus-engine/compile/from-base-npc-static-inventory';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { absorbInventoryMetadata, mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import { fromBaseRecord, fromBaseUuid } from '../janus-engine/compile/from-base-context';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { readLayerListEntryUuid, readInventoryField } from '../janus-engine/compile/inventory-raw-readers';
import { createFormulaReadContext } from '../janus-engine/compile/formula-read-context';
import type { SpawnSourcePackage } from '../janus-engine/spawn/source-plan';
import type { Row } from '../janus-engine/spawn/row-plan';
import type { SpawnPreviewInput } from '../janus-engine/spawn/preview';

export interface EquipmentPreviewSession { update(draft: Row): Promise<Row> }
type Load = (ids: readonly string[], templates: readonly string[], enhancements: readonly string[]) => Promise<SpawnSourcePackage>;

const draftReaders = createFormulaReadContext({ catalogRows: {}, abilityTypes: [], aggregationStrategy: () => 'host_wins',
    resolveCarrierRef: () => null, resolveInventoryInstance: () => null });
const draftEntries = (draft: Row, name: string): unknown[] => Object.values(draftReaders.layerEntries(draft, name));

function requested(draft: Row): { ids: string[]; enhancements: string[] } {
    const ids = new Set<string>(), enhancements = new Set<string>();
    const list = draftEntries(draft, 'inventory_equipped');
    for (const entry of list) {
        const id = fromBaseRecord(entry) ? readItemField(entry, 'item_id') : readLayerListEntryUuid(entry);
        if (fromBaseUuid(id)) ids.add(id.toUpperCase());
        const template = fromBaseRecord(entry) ? readItemField(entry, 'template_id') : typeof entry === 'string' ? entry.split('|')[1] : null;
        if (fromBaseUuid(template)) enhancements.add(template.toUpperCase());
    }
    const selected = draftEntries(draft, 'inventory_equipped_source');
    for (const entry of selected) {
        const id = readLayerListEntryUuid(entry); if (fromBaseUuid(id)) ids.add(id.toUpperCase());
    }
    return { ids: [...ids], enhancements: [...enhancements] };
}

/** One authoring mount owns its native source, definitions and inventory array.
 * Only newly requested closures enter the session after initial compilation. */
export async function createEquipmentPreviewSession(itemId: string, initial: Row, load: Load,
    preview: (input: SpawnPreviewInput) => Promise<Row>): Promise<EquipmentPreviewSession> {
    const inputs = requested(initial);
    const data = await load([itemId, ...inputs.ids], [], inputs.enhancements);
    const base = Object.keys(data.shared).length ? mergeInventoryMetadata(data.shared, data.base) : data.base;
    let metadata = entityRuntimeMetadata(mergeInventoryMetadata(data.metadata, base.inventory_metadata));
    const host = await preview({ data, itemId, draft: {}, template: null, sourceOnly: true });
    Object.defineProperty(host, '_definition_input_revs', { get: () => base.dependency_revs, configurable: true });
    const bucket = fromBaseRecord(host.item_layer_types) ? host.item_layer_types : (host.item_layer_types = {}) as Row;
    const sourceContext = createFromBaseNpcContext(base, metadata.player!, metadata.inventory!);
    const authored = sourceContext.definition(itemId);
    const originalEquipped = Object.values(sourceContext.ownReaders.layerEntries(authored, 'inventory_equipped'));
    const originalSource = Object.values(sourceContext.ownReaders.layerEntries(authored, 'inventory_equipped_source'));
    const computed = fromBaseRecord(host.computed) ? host.computed : (host.computed = {}) as Row;
    const mt = fromBaseRecord(computed.mt) ? computed.mt : (computed.mt = {}) as Row;
    const direct = fromBaseRecord(mt.direct_loadout_override) ? mt.direct_loadout_override : (mt.direct_loadout_override = {}) as Row;
    const originalDirect = readItemField(direct, 'inventory_equipped');
    // Saved inventory belongs to actual instances; a definition preview projects
    // static instances only. Keep the array and root catalog identities throughout.
    const inventory = Array.isArray(host.inventory) ? host.inventory as Row[] : [];
    host.inventory = inventory;
    host.catalog = fromBaseRecord(host.catalog) ? host.catalog : {};
    const ownedInputs = new WeakSet<object>([base, ...fromBaseRecord(base.catalog) ? [base.catalog] : []]);
    const heldInstances = new Map<string, Row>();
    let project = createNpcWornInventoryProjector(base, metadata.player!, metadata.inventory!, true, heldInstances);
    const displayRows = new WeakMap<Row, Row>();
    const equippedDisplay: Row[] = [], unequippedDisplay: Row[] = [];
    let previous = '';
    let chain = Promise.resolve();
    const update = async (draft: Row): Promise<Row> => {
        const key = JSON.stringify(draft);
        if (key === previous) return host;
        const need = requested(draft), catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
        const missing = need.ids.filter(id => !catalog[id]);
        const templates = Array.isArray(base.enchantment_templates) ? base.enchantment_templates : [];
        const missingTemplates = need.enhancements.filter(id => !templates.some(row => fromBaseRecord(row) && readItemField(row, 'enchantment_template_id') === id));
        if (missing.length || missingTemplates.length) {
            const additional = await load(missing, [], missingTemplates);
            // Existing leaves stay owned by the held package; extend only missing
            // closure branches. Projector contexts may now see the new definitions.
            absorbInventoryMetadata(base, additional.shared, base, ownedInputs);
            absorbInventoryMetadata(base, additional.base, base, ownedInputs);
            metadata = entityRuntimeMetadata(mergeInventoryMetadata(data.metadata, base.inventory_metadata));
            project = createNpcWornInventoryProjector(base, metadata.player!, metadata.inventory!, true, heldInstances);
        }
        const explicit = Object.prototype.hasOwnProperty.call(draft, 'inventory_equipped');
        const rows = draftEntries(draft, 'inventory_equipped');
        if (explicit) bucket.inventory_equipped = rows.map(entry => fromBaseRecord(entry)
            ? { item_layer_effect: readItemField(entry, 'item_id'), template_id: readItemField(entry, 'template_id'), qty: readItemField(entry, 'qty') } : entry);
        else bucket.inventory_equipped = originalEquipped;
        if (Object.prototype.hasOwnProperty.call(draft, 'inventory_equipped_source')) bucket.inventory_equipped_source = draftEntries(draft, 'inventory_equipped_source');
        else bucket.inventory_equipped_source = originalSource;
        // An explicit authored empty suppresses all provider loadouts.
        direct.inventory_equipped = explicit || Boolean(originalDirect);
        const next = project(host);
        if (next !== inventory) inventory.splice(0, inventory.length, ...next);
        // The grid's cold readers resolve new equipment against this same catalog.
        const heldCatalog = host.catalog as Row;
        for (const node of inventory) {
            const id = readItemField(node, 'item_id');
            if (typeof id === 'string' && !heldCatalog[id]) heldCatalog[id] = node;
        }
        const equipped: Row[] = [], unequipped: Row[] = [];
        for (const node of inventory) {
            let display = displayRows.get(node);
            if (!display) { display = { item_arr: node }; displayRows.set(node, display); }
            (readInventoryField(node, 'equipped') ? equipped : unequipped).push(display);
        }
        equippedDisplay.splice(0, equippedDisplay.length, ...equipped);
        unequippedDisplay.splice(0, unequippedDisplay.length, ...unequipped);
        bucket.inventory_equipped = equippedDisplay;
        bucket.inventory_unequipped = unequippedDisplay;
        previous = key;
        return host;
    };
    return { update(draft) {
        const pending = chain.then(() => update(draft));
        chain = pending.then(() => undefined, () => undefined);
        return pending;
    } };
}
